
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import FadeIn from '../../components/FadeIn';
import { ArrowLeft, Save, User } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { triggerAutoUpdate } from '../../utils/sitemapService';

const AuthorEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [bio, setBio] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [image, setImage] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [slug] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAuthor = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, 'authors', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setName(data.name);
                    setRole(data.role || '');
                    setBio(data.bio || '');
                    setLinkedin(data.linkedin || '');
                    setAvatarUrl(data.avatarUrl || '');
                } else {
                    setError('Author not found');
                }
            } catch (err) {
                setError('Error fetching author: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        if (isEditing) {
            fetchAuthor();
        }
    }, [id, isEditing]);

    const handleImageUpload = async (file) => {
        if (!file) return null;
        const storageRef = ref(storage, `authors/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let url = avatarUrl;
            if (image) {
                url = await handleImageUpload(image);
            }

            // Generate slug if not present or name changed (simplified logic: always regenerate from name if new, or keep existing)
            // Ideally should check for uniqueness, but for now assuming name uniqueness or manual management.
            let authorSlug = slug;
            if (!authorSlug || !isEditing) {
                authorSlug = name.toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)+/g, '');
            }

            const authorData = {
                name,
                role,
                bio,
                linkedin,
                avatarUrl: url,
                slug: authorSlug,
                updatedAt: serverTimestamp()
            };

            if (isEditing) {
                await updateDoc(doc(db, 'authors', id), authorData);
            } else {
                authorData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'authors'), authorData);
            }

            // Trigger Sitemap Update
            await triggerAutoUpdate();

            navigate('/admin/dashboard', { state: { activePanel: 'authors' } });
        } catch (err) {
            console.error(err);
            setError('Error saving author: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--bg)' }}>
            <div className="max-w-3xl mx-auto">
                <FadeIn>
                    <div className="flex items-center mb-8 space-x-4">
                        <Link to="/admin/dashboard" state={{ activePanel: 'authors' }} style={{ color: 'var(--muted)' }} className="hover:text-white transition-colors">
                            <ArrowLeft size={24} />
                        </Link>
                        <h1 className="text-3xl font-bold text-white">
                            {isEditing ? 'Edit Author' : 'New Author'}
                        </h1>
                    </div>

                    <div className="rounded-xl shadow-2xl p-8 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                        {error && <div className="bg-red-900/40 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-6">{error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex items-center space-x-6 mb-8">
                                <div className="shrink-0 relative">
                                    {avatarUrl || image ? (
                                        <img
                                            src={image ? URL.createObjectURL(image) : avatarUrl}
                                            alt="Preview"
                                            className="h-24 w-24 object-cover rounded-full border-4 shadow-xl"
                                            style={{ borderColor: 'var(--surface2)' }}
                                        />
                                    ) : (
                                        <div className="h-24 w-24 rounded-full flex items-center justify-center text-gray-500 border-4 shadow-inner" style={{ background: 'var(--surface2)', borderColor: 'var(--surface3)' }}>
                                            <User size={40} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Profile Photo</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setImage(e.target.files[0])}
                                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#A2CB52]/10 file:text-[#A2CB52] hover:file:bg-[#A2CB52]/20"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                        placeholder="e.g. Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Role / Title</label>
                                    <input
                                        type="text"
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                        placeholder="e.g. Senior Content Writer"
                                    />
                                </div>
                            </div>

                            {/* Slug Field (Read Only or Editable) */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>URL Slug (Auto-generated)</label>
                                <input
                                    type="text"
                                    value={slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')}
                                    readOnly
                                    className="w-full px-4 py-3 rounded-lg border focus:outline-none cursor-not-allowed font-mono text-sm"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--muted)' }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Bio</label>
                                <textarea
                                    rows="4"
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text2)' }}
                                    placeholder="Short biography..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>LinkedIn URL</label>
                                <input
                                    type="url"
                                    value={linkedin}
                                    onChange={(e) => setLinkedin(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                    placeholder="https://linkedin.com/in/..."
                                />
                            </div>

                            <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center px-8 py-3 rounded-lg font-bold transition-all shadow-lg disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98]"
                                    style={{ backgroundColor: 'var(--accent)', color: '#000' }}
                                >
                                    <Save size={20} className="mr-2" />
                                    {loading ? 'Saving...' : 'Save Author'}
                                </button>
                            </div>
                        </form>
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default AuthorEditor;
